// ============================================================================
// GumaHUB Navigation Sidebar
// A refined, hierarchical navigation with collapsible modules
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@gumahub/authentication';
import { useTranslation } from 'react-i18next';
import { useBranding } from '../contexts/BrandingContext';

// ============================================================================
// Types
// ============================================================================

interface NavItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  path?: string;
  children?: NavItem[];
  badge?: number | string;
  color?: string;
  requiredModule?: string;
  dividerBefore?: boolean;
  dividerAfter?: boolean;
}

interface NavSectionProps {
  title?: string;
  items: NavItem[];
  collapsed: boolean;
  expandedGroups: Record<string, boolean>;
  onToggleGroup: (id: string) => void;
  depth?: number;
}

interface NavigationSidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

// ============================================================================
// Icons - Refined stroke-based icons
// ============================================================================

const Icons = {
  // Dashboard
  Dashboard: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  ),

  // Enterprise
  Building: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18" />
      <path d="M5 21V7l8-4v18" />
      <path d="M19 21V11l-6-4" />
      <path d="M9 9v.01" />
      <path d="M9 12v.01" />
      <path d="M9 15v.01" />
      <path d="M9 18v.01" />
    </svg>
  ),

  // Companies/Clients
  Buildings: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 22V4a2 2 0 012-2h8a2 2 0 012 2v18Z" />
      <path d="M6 12H4a2 2 0 00-2 2v6a2 2 0 002 2h2" />
      <path d="M18 9h2a2 2 0 012 2v9a2 2 0 01-2 2h-2" />
      <path d="M10 6h4" />
      <path d="M10 10h4" />
      <path d="M10 14h4" />
      <path d="M10 18h4" />
    </svg>
  ),

  // Users
  Users: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),

  // Orders
  ShoppingCart: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="21" r="1" />
      <circle cx="19" cy="21" r="1" />
      <path d="M2.05 2.05h2l2.66 12.42a2 2 0 002 1.58h9.78a2 2 0 001.95-1.57l1.65-7.43H5.12" />
    </svg>
  ),

  // Order List
  ClipboardList: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
      <path d="M12 11h4" />
      <path d="M12 16h4" />
      <path d="M8 11h.01" />
      <path d="M8 16h.01" />
    </svg>
  ),

  // Parts/Cross Reference
  ArrowsExchange: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3L4 7l4 4" />
      <path d="M4 7h16" />
      <path d="M16 21l4-4-4-4" />
      <path d="M20 17H4" />
    </svg>
  ),

  // Shipments
  Truck: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 17h4V5H2v12h3" />
      <path d="M20 17h2v-3.34a4 4 0 00-1.17-2.83L19 9h-5v8h1" />
      <circle cx="7.5" cy="17.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  ),

  // Package/HU
  Package: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M16.5 9.4l-9-5.19" />
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <polyline points="3.27,6.96 12,12.01 20.73,6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),

  // Packing
  BoxOpen: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </svg>
  ),

  // HR / Employees
  UserGroup: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 21a8 8 0 00-16 0" />
      <circle cx="10" cy="8" r="5" />
      <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 00-.45-8.3" />
    </svg>
  ),

  // Employee/Person
  User: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21v-2a4 4 0 00-4-4H9a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),

  // Department
  Sitemap: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2" width="6" height="4" rx="1" />
      <rect x="2" y="14" width="6" height="4" rx="1" />
      <rect x="16" y="14" width="6" height="4" rx="1" />
      <path d="M12 6v4" />
      <path d="M5 10h14" />
      <path d="M5 10v4" />
      <path d="M19 10v4" />
    </svg>
  ),

  // Org Chart
  OrgChart: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2" width="6" height="4" rx="1" />
      <rect x="2" y="18" width="5" height="4" rx="1" />
      <rect x="9.5" y="18" width="5" height="4" rx="1" />
      <rect x="17" y="18" width="5" height="4" rx="1" />
      <path d="M12 6v6" />
      <path d="M4.5 12h15" />
      <path d="M4.5 12v6" />
      <path d="M12 12v6" />
      <path d="M19.5 12v6" />
    </svg>
  ),

  // My Leave/Calendar
  CalendarDays: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <path d="M8 14h.01" />
      <path d="M12 14h.01" />
      <path d="M16 14h.01" />
      <path d="M8 18h.01" />
      <path d="M12 18h.01" />
    </svg>
  ),

  // Team Calendar
  CalendarUsers: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 2v4" />
      <path d="M8 2v4" />
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M3 10h18" />
      <path d="M10 16a2 2 0 11-4 0 2 2 0 014 0z" />
      <path d="M18 16a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),

  // Travel/Plane
  Plane: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
    </svg>
  ),

  // Expense/Receipt
  Receipt: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z" />
      <path d="M8 10h8" />
      <path d="M8 14h4" />
    </svg>
  ),

  // Approvals/CheckCircle
  CheckCircle: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <polyline points="22,4 12,14.01 9,11.01" />
    </svg>
  ),

  // Settings/Cog
  Settings: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),

  // Master Data/Database
  Database: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
    </svg>
  ),

  // Bid Management
  Gavel: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 12.5l-5 5" />
      <path d="M3 21l4.5-4.5" />
      <path d="M14.5 2.5l7 7-2.12 2.12-7-7z" />
      <path d="M10.38 6.62L3.5 13.5l7 7 6.88-6.88" />
    </svg>
  ),

  // Reports/Chart
  BarChart: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  ),

  // Chevrons
  ChevronRight: () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9,18 15,12 9,6" />
    </svg>
  ),

  ChevronDown: () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6,9 12,15 18,9" />
    </svg>
  ),

  ChevronLeft: () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15,18 9,12 15,6" />
    </svg>
  ),

  // Collapse sidebar
  PanelLeftClose: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18" />
      <path d="M16 15l-3-3 3-3" />
    </svg>
  ),

  PanelLeftOpen: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18" />
      <path d="M14 9l3 3-3 3" />
    </svg>
  ),

  // Profile
  UserCircle: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="10" r="3" />
      <path d="M7 20.662V19a2 2 0 012-2h6a2 2 0 012 2v1.662" />
    </svg>
  ),

  // Logout
  LogOut: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <polyline points="16,17 21,12 16,7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),

  // Branding/Palette
  Palette: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r="0.5" fill="currentColor" />
      <circle cx="17.5" cy="10.5" r="0.5" fill="currentColor" />
      <circle cx="8.5" cy="7.5" r="0.5" fill="currentColor" />
      <circle cx="6.5" cy="12.5" r="0.5" fill="currentColor" />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 011.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.555C21.965 6.012 17.461 2 12 2z" />
    </svg>
  ),

  // Integration/Link
  Link: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  ),

  // Employment types
  Briefcase: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
    </svg>
  ),

  // Absence types
  CalendarOff: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.2 4.2A2 2 0 003 6v14a2 2 0 002 2h14a2 2 0 001.82-1.18" />
      <path d="M21 15.5V6a2 2 0 00-2-2H9.5" />
      <path d="M16 2v4" />
      <path d="M3 10h7" />
      <path d="M21 10h-5.5" />
      <path d="M2 2l20 20" />
    </svg>
  ),

  // Policies
  FileText: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10,9 9,9 8,9" />
    </svg>
  ),

  // Documents / Folder
  Folder: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 20a2 2 0 01-2-2V6a2 2 0 012-2h4l2 3h8a2 2 0 012 2v9a2 2 0 01-2 2Z" />
    </svg>
  ),

  // Shared documents (folder with people)
  FolderShared: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 9V6a2 2 0 012-2h4l2 3h8a2 2 0 012 2v3" />
      <circle cx="12" cy="15" r="2" />
      <path d="M16 21a4 4 0 00-8 0" />
    </svg>
  ),

  // Service / field service
  Wrench: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a4 4 0 00-5.4 5.2L3 17.8 6.2 21l6.3-6.3a4 4 0 005.2-5.4l-2.6 2.6-2.1-.5-.5-2.1Z" />
    </svg>
  ),

  // Expense types
  Tags: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H2v7l6.29 6.29c.94.94 2.48.94 3.42 0l3.58-3.58c.94-.94.94-2.48 0-3.42L9 5Z" />
      <path d="M6 9.01V9" />
      <path d="M15 5l6.3 6.3a2.4 2.4 0 010 3.4L17 19" />
    </svg>
  ),

  // Training/Book
  Book: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
      <path d="M8 7h8" />
      <path d="M8 11h6" />
    </svg>
  ),

  // Academic cap/graduation
  AcademicCap: () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c0 2 6 3 6 3s6-1 6-3v-5" />
    </svg>
  ),
};

// ============================================================================
// Module Colors
// ============================================================================

const MODULE_COLORS: Record<string, { accent: string; bg: string; text: string; hover: string; darkBg: string; darkText: string; darkHover: string }> = {
  enterprise: { accent: 'bg-guma-500', bg: 'bg-guma-50', text: 'text-guma-600', hover: 'hover:bg-guma-50', darkBg: 'dark:bg-guma-500/10', darkText: 'dark:text-guma-400', darkHover: 'dark:hover:bg-guma-500/10' },
  orders: { accent: 'bg-ocean-500', bg: 'bg-ocean-50', text: 'text-ocean-600', hover: 'hover:bg-ocean-50', darkBg: 'dark:bg-[#8ab4f8]/10', darkText: 'dark:text-[#8ab4f8]', darkHover: 'dark:hover:bg-[#8ab4f8]/10' },
  shipments: { accent: 'bg-mint-500', bg: 'bg-mint-50', text: 'text-mint-600', hover: 'hover:bg-mint-50', darkBg: 'dark:bg-mint-500/10', darkText: 'dark:text-mint-400', darkHover: 'dark:hover:bg-mint-500/10' },
  hr: { accent: 'bg-purple-500', bg: 'bg-purple-50', text: 'text-purple-600', hover: 'hover:bg-purple-50', darkBg: 'dark:bg-purple-500/10', darkText: 'dark:text-purple-400', darkHover: 'dark:hover:bg-purple-500/10' },
  travel: { accent: 'bg-cyan-500', bg: 'bg-cyan-50', text: 'text-cyan-600', hover: 'hover:bg-cyan-50', darkBg: 'dark:bg-cyan-500/10', darkText: 'dark:text-cyan-400', darkHover: 'dark:hover:bg-cyan-500/10' },
  expense: { accent: 'bg-pink-500', bg: 'bg-pink-50', text: 'text-pink-600', hover: 'hover:bg-pink-50', darkBg: 'dark:bg-pink-500/10', darkText: 'dark:text-pink-400', darkHover: 'dark:hover:bg-pink-500/10' },
  training: { accent: 'bg-violet-500', bg: 'bg-violet-50', text: 'text-violet-600', hover: 'hover:bg-violet-50', darkBg: 'dark:bg-violet-500/10', darkText: 'dark:text-violet-400', darkHover: 'dark:hover:bg-violet-500/10' },
  bid: { accent: 'bg-amber-500', bg: 'bg-amber-50', text: 'text-amber-600', hover: 'hover:bg-amber-50', darkBg: 'dark:bg-amber-500/10', darkText: 'dark:text-amber-400', darkHover: 'dark:hover:bg-amber-500/10' },
  reports: { accent: 'bg-indigo-500', bg: 'bg-indigo-50', text: 'text-indigo-600', hover: 'hover:bg-indigo-50', darkBg: 'dark:bg-indigo-500/10', darkText: 'dark:text-indigo-400', darkHover: 'dark:hover:bg-indigo-500/10' },
  documents: { accent: 'bg-teal-500', bg: 'bg-teal-50', text: 'text-teal-600', hover: 'hover:bg-teal-50', darkBg: 'dark:bg-teal-500/10', darkText: 'dark:text-teal-400', darkHover: 'dark:hover:bg-teal-500/10' },
  service: { accent: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-600', hover: 'hover:bg-emerald-50', darkBg: 'dark:bg-emerald-500/10', darkText: 'dark:text-emerald-400', darkHover: 'dark:hover:bg-emerald-500/10' },
  settings: { accent: 'bg-slate-500', bg: 'bg-slate-50', text: 'text-slate-600', hover: 'hover:bg-slate-50', darkBg: 'dark:bg-[#3c3c3c]', darkText: 'dark:text-[#e8eaed]', darkHover: 'dark:hover:bg-[#3c3c3c]' },
};

// ============================================================================
// Navigation Item Component
// ============================================================================

const NavItemComponent: React.FC<{
  item: NavItem;
  collapsed: boolean;
  expandedGroups: Record<string, boolean>;
  onToggleGroup: (id: string) => void;
  depth?: number;
}> = ({ item, collapsed, expandedGroups, onToggleGroup, depth = 0 }) => {
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedGroups[item.id] ?? true;
  const colors = MODULE_COLORS[item.color || 'settings'];

  // Check if any child is active
  const isChildActive = useMemo(() => {
    if (!item.children) return false;
    const checkActive = (items: NavItem[]): boolean => {
      return items.some(child => {
        if (child.path && location.pathname.startsWith(child.path)) return true;
        if (child.children) return checkActive(child.children);
        return false;
      });
    };
    return checkActive(item.children);
  }, [item.children, location.pathname]);

  // Render link item
  if (item.path && !hasChildren) {
    return (
      <>
        {item.dividerBefore && (
          <div className={`my-3 mx-3 h-px bg-gradient-to-r from-transparent via-slate-200 dark:via-[#3c4043] to-transparent`} />
        )}
        <NavLink
          to={item.path}
          className={({ isActive }) => {
            const baseClasses = `group relative flex items-center gap-3 rounded-xl transition-all duration-200 ${
              depth > 0 ? 'py-2 pl-11 pr-3' : 'py-2.5 px-3'
            }`;

            if (isActive) {
              return `${baseClasses} ${colors.bg} ${colors.darkBg} ${colors.text} ${colors.darkText} font-medium`;
            }
            return `${baseClasses} text-slate-600 dark:text-[#9aa0a6] hover:bg-slate-100 dark:hover:bg-[#3c3c3c] hover:text-slate-900 dark:hover:text-[#e8eaed]`;
          }}
        >
          {({ isActive }) => (
            <>
              {/* Active indicator bar */}
              {isActive && depth === 0 && (
                <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 ${colors.accent} rounded-r-full transition-all duration-200`} />
              )}

              {/* Icon */}
              {item.icon && !collapsed && (
                <span className={`flex-shrink-0 transition-transform duration-200 group-hover:scale-110 ${isActive ? `${colors.text} ${colors.darkText}` : ''}`}>
                  {item.icon}
                </span>
              )}

              {/* Collapsed state icon only */}
              {item.icon && collapsed && (
                <span className={`flex-shrink-0 transition-transform duration-200 group-hover:scale-110 ${isActive ? `${colors.text} ${colors.darkText}` : ''}`}>
                  {item.icon}
                </span>
              )}

              {/* Label */}
              {!collapsed && (
                <span className="flex-1 truncate text-sm">{item.label}</span>
              )}

              {/* Badge */}
              {item.badge && !collapsed && (
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors.bg} ${colors.darkBg} ${colors.text} ${colors.darkText}`}>
                  {item.badge}
                </span>
              )}

              {/* Tooltip for collapsed state */}
              {collapsed && (
                <div className="absolute left-full ml-3 px-3 py-2 bg-slate-900 dark:bg-[#3c3c3c] text-white dark:text-[#e8eaed] text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[100] shadow-lg">
                  {item.label}
                  <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent border-r-slate-900 dark:border-r-[#3c3c3c]" />
                </div>
              )}
            </>
          )}
        </NavLink>
        {item.dividerAfter && (
          <div className={`my-3 mx-3 h-px bg-gradient-to-r from-transparent via-slate-200 dark:via-[#3c4043] to-transparent`} />
        )}
      </>
    );
  }

  // Render group header
  if (hasChildren) {
    return (
      <div className="relative">
        {item.dividerBefore && (
          <div className={`my-3 mx-3 h-px bg-gradient-to-r from-transparent via-slate-200 dark:via-[#3c4043] to-transparent`} />
        )}

        <button
          onClick={() => onToggleGroup(item.id)}
          className={`w-full group relative flex items-center gap-3 py-2.5 px-3 rounded-xl transition-all duration-200 ${
            isChildActive
              ? `${colors.bg} ${colors.darkBg} ${colors.text} ${colors.darkText}`
              : 'text-slate-600 dark:text-[#9aa0a6] hover:bg-slate-100 dark:hover:bg-[#3c3c3c] hover:text-slate-900 dark:hover:text-[#e8eaed]'
          } ${collapsed ? 'justify-center' : ''}`}
        >
          {/* Active indicator bar */}
          {isChildActive && !collapsed && (
            <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 ${colors.accent} rounded-r-full transition-all duration-200`} />
          )}

          {/* Icon */}
          {item.icon && (
            <span className={`flex-shrink-0 transition-transform duration-200 group-hover:scale-110 ${isChildActive ? `${colors.text} ${colors.darkText}` : ''}`}>
              {item.icon}
            </span>
          )}

          {/* Label */}
          {!collapsed && (
            <>
              <span className={`flex-1 text-left truncate text-sm ${isChildActive ? 'font-medium' : ''}`}>
                {item.label}
              </span>

              {/* Expand/collapse chevron */}
              <span className={`transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}>
                <Icons.ChevronDown />
              </span>
            </>
          )}

          {/* Tooltip for collapsed state */}
          {collapsed && (
            <div className="absolute left-full ml-3 px-3 py-2 bg-slate-900 dark:bg-[#3c3c3c] text-white dark:text-[#e8eaed] text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[100] shadow-lg">
              {item.label}
              <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent border-r-slate-900 dark:border-r-[#3c3c3c]" />
            </div>
          )}
        </button>

        {/* Children */}
        {!collapsed && (
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="mt-1 space-y-0.5">
              {item.children?.map((child) => (
                <NavItemComponent
                  key={child.id}
                  item={child}
                  collapsed={collapsed}
                  expandedGroups={expandedGroups}
                  onToggleGroup={onToggleGroup}
                  depth={depth + 1}
                />
              ))}
            </div>
          </div>
        )}

        {item.dividerAfter && (
          <div className={`my-3 mx-3 h-px bg-gradient-to-r from-transparent via-slate-200 dark:via-[#3c4043] to-transparent`} />
        )}
      </div>
    );
  }

  return null;
};

// ============================================================================
// Navigation Section Component
// ============================================================================

const NavSection: React.FC<NavSectionProps> = ({
  title,
  items,
  collapsed,
  expandedGroups,
  onToggleGroup,
}) => {
  return (
    <div className="mb-2">
      {/* Section title */}
      {title && !collapsed && (
        <div className="px-3 py-2 mb-1">
          <span className="text-[10px] font-bold text-slate-400 dark:text-[#9aa0a6] uppercase tracking-[0.1em]">
            {title}
          </span>
        </div>
      )}

      {/* Section divider for collapsed state */}
      {title && collapsed && (
        <div className="my-3 mx-2 h-px bg-slate-200 dark:bg-[#3c4043]" />
      )}

      {/* Items */}
      <div className="space-y-0.5">
        {items.map((item) => (
          <NavItemComponent
            key={item.id}
            item={item}
            collapsed={collapsed}
            expandedGroups={expandedGroups}
            onToggleGroup={onToggleGroup}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Main Navigation Sidebar Component
// ============================================================================

const NavigationSidebar: React.FC<NavigationSidebarProps> = ({
  collapsed,
  onToggleCollapse,
}) => {
  const { t } = useTranslation();
  const { user, logout, getAccessibleModules } = useAuth();
  const { branding, clearBranding } = useBranding();
  const accessibleModules = getAccessibleModules();

  // Handle logout - clear branding and then logout
  const handleLogout = useCallback(() => {
    clearBranding();
    logout();
  }, [clearBranding, logout]);

  // Expanded groups state with localStorage persistence
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem('gumahub_nav_expanded');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Save to localStorage when expanded groups change
  useEffect(() => {
    localStorage.setItem('gumahub_nav_expanded', JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  const toggleGroup = useCallback((id: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [id]: prev[id] === undefined ? false : !prev[id],
    }));
  }, []);

  // Build navigation items based on user permissions
  const navigationItems = useMemo((): NavItem[] => {
    const items: NavItem[] = [];

    // Dashboard
    items.push({
      id: 'dashboard',
      label: t('nav.dashboard', 'Dashboard'),
      icon: <Icons.Dashboard />,
      path: '/',
      color: 'settings',
    });

    // Enterprise Module
    if (accessibleModules.includes('ENT')) {
      items.push({
        id: 'enterprise',
        label: t('nav.enterprise', 'Enterprise'),
        icon: <Icons.Building />,
        color: 'enterprise',
        dividerBefore: true,
        children: [
          {
            id: 'ent-clients',
            label: t('nav.clients', 'Clients'),
            icon: <Icons.Buildings />,
            path: '/enterprise',
            color: 'enterprise',
          },
          {
            id: 'ent-users',
            label: t('nav.users', 'Users'),
            icon: <Icons.Users />,
            path: '/enterprise/users',
            color: 'enterprise',
          },
          {
            id: 'ent-branding',
            label: t('branding.settings.title', 'Company Branding'),
            icon: <Icons.Palette />,
            path: '/settings/company/branding',
            color: 'enterprise',
            dividerBefore: true,
          },
        ],
      });
    }

    // Orders Module
    if (accessibleModules.includes('ORD')) {
      items.push({
        id: 'orders',
        label: t('nav.orders', 'Orders'),
        icon: <Icons.ShoppingCart />,
        color: 'orders',
        children: [
          {
            id: 'ord-list',
            label: t('nav.orderList', 'Order List'),
            icon: <Icons.ClipboardList />,
            path: '/orders',
            color: 'orders',
          },
          {
            id: 'ord-settings',
            label: t('nav.basicData', 'Basic Data'),
            icon: <Icons.Database />,
            color: 'orders',
            children: [
              {
                id: 'ord-parts-xref',
                label: t('nav.partsCrossRef', 'Part Cross References'),
                icon: <Icons.ArrowsExchange />,
                path: '/settings/order/parts-cross-ref',
                color: 'orders',
              },
            ],
          },
        ],
      });
    }

    // Shipments Module
    if (accessibleModules.includes('SHP')) {
      items.push({
        id: 'shipments',
        label: t('nav.shipments', 'Shipments'),
        icon: <Icons.Truck />,
        color: 'shipments',
        children: [
          {
            id: 'shp-list',
            label: t('nav.shipmentList', 'Shipment List'),
            icon: <Icons.Truck />,
            path: '/shipments',
            color: 'shipments',
          },
          {
            id: 'shp-settings',
            label: t('nav.basicData', 'Basic Data'),
            icon: <Icons.Database />,
            color: 'shipments',
            children: [
              {
                id: 'shp-hu-types',
                label: t('nav.huTypes', 'Handling Unit Types'),
                icon: <Icons.Package />,
                path: '/settings/shipment/hu-types',
                color: 'shipments',
              },
              {
                id: 'shp-packing',
                label: t('nav.packingInstructions', 'Packing Instructions'),
                icon: <Icons.BoxOpen />,
                path: '/settings/shipment/packing-instructions',
                color: 'shipments',
              },
            ],
          },
        ],
      });
    }

    // HR Module with self-service separation
    if (accessibleModules.includes('EMP')) {
      items.push({
        id: 'hr',
        label: t('nav.humanResources', 'Human Resources'),
        icon: <Icons.UserGroup />,
        color: 'hr',
        children: [
          // Self-service section
          {
            id: 'hr-self-service',
            label: t('nav.selfService', 'Self-Service'),
            icon: <Icons.UserCircle />,
            path: '/me',
            color: 'hr',
          },
          {
            id: 'hr-my-profile',
            label: t('nav.myProfile', 'My Profile'),
            icon: <Icons.UserCircle />,
            path: '/employees/me',
            color: 'hr',
          },
          {
            id: 'hr-my-leave',
            label: t('nav.myLeave', 'My Leave'),
            icon: <Icons.CalendarDays />,
            path: '/absence',
            color: 'hr',
          },
          {
            id: 'hr-my-goals',
            label: t('nav.myGoals', 'My Goals'),
            icon: <Icons.CheckCircle />,
            path: '/performance',
            color: 'hr',
          },
          {
            id: 'hr-my-reviews',
            label: t('nav.myReviews', 'My Reviews'),
            icon: <Icons.CheckCircle />,
            path: '/performance/reviews',
            color: 'hr',
          },
          {
            id: 'hr-my-competencies',
            label: t('nav.myCompetencies', 'My Competencies'),
            icon: <Icons.CheckCircle />,
            path: '/performance/competencies',
            color: 'hr',
          },
          {
            id: 'hr-my-travel',
            label: t('nav.myTravel', 'My Travel'),
            icon: <Icons.Plane />,
            path: '/travel',
            color: 'travel',
          },
          {
            id: 'hr-my-expenses',
            label: t('nav.myExpenses', 'My Expenses'),
            icon: <Icons.Receipt />,
            path: '/expense',
            color: 'expense',
          },
          {
            id: 'hr-my-training',
            label: t('nav.myTraining', 'My Training'),
            icon: <Icons.AcademicCap />,
            path: '/training/my-training',
            color: 'training',
          },
          {
            id: 'hr-certificates',
            label: t('nav.certificates', 'Certificates'),
            icon: <Icons.AcademicCap />,
            path: '/training/certificates',
            color: 'training',
          },
          {
            id: 'hr-training-catalog',
            label: t('nav.trainingCatalog', 'Training Catalog'),
            icon: <Icons.Book />,
            path: '/training/catalog',
            color: 'training',
          },
          // Team/Manager section
          {
            id: 'hr-team-goals',
            label: t('nav.teamGoals', 'Team Goals'),
            icon: <Icons.CheckCircle />,
            path: '/performance/team',
            color: 'hr',
            dividerBefore: true,
          },
          {
            id: 'hr-team-reviews',
            label: t('nav.teamReviews', 'Team Reviews'),
            icon: <Icons.CheckCircle />,
            path: '/performance/team-reviews',
            color: 'hr',
          },
          {
            id: 'hr-review-cycles',
            label: t('nav.reviewCycles', 'Review Cycles'),
            icon: <Icons.CheckCircle />,
            path: '/performance/cycles',
            color: 'hr',
          },
          {
            id: 'hr-calibration',
            label: t('nav.calibration', 'Calibration (9-box)'),
            icon: <Icons.CheckCircle />,
            path: '/performance/calibration',
            color: 'hr',
          },
          {
            id: 'hr-team-calendar',
            label: t('nav.teamCalendar', 'Team Calendar'),
            icon: <Icons.CalendarUsers />,
            path: '/absence/calendar',
            color: 'hr',
          },
          {
            id: 'hr-approvals',
            label: t('nav.pendingApprovals', 'Pending Approvals'),
            icon: <Icons.CheckCircle />,
            path: '/absence/approvals',
            color: 'hr',
          },
          {
            id: 'hr-travel-approvals',
            label: t('nav.travelApprovals', 'Travel Approvals'),
            icon: <Icons.CheckCircle />,
            path: '/travel/approvals',
            color: 'travel',
          },
          {
            id: 'hr-expense-approvals',
            label: t('nav.expenseApprovals', 'Expense Approvals'),
            icon: <Icons.CheckCircle />,
            path: '/expense/approvals',
            color: 'expense',
          },
          // Admin section
          {
            id: 'hr-dashboard',
            label: t('nav.hrDashboard', 'HR Dashboard'),
            icon: <Icons.Dashboard />,
            path: '/hr/dashboard',
            color: 'hr',
            dividerBefore: true,
          },
          {
            id: 'hr-employees',
            label: t('nav.employees', 'Employees'),
            icon: <Icons.Users />,
            path: '/employees',
            color: 'hr',
          },
          {
            id: 'hr-departments',
            label: t('nav.departments', 'Departments'),
            icon: <Icons.Sitemap />,
            path: '/employees/departments',
            color: 'hr',
          },
          {
            id: 'hr-org-chart',
            label: t('nav.orgChart', 'Organization Chart'),
            icon: <Icons.OrgChart />,
            path: '/employees/org-chart',
            color: 'hr',
          },
          {
            id: 'hr-positions',
            label: t('nav.positions', 'Positions'),
            icon: <Icons.Building />,
            path: '/positions',
            color: 'hr',
          },
          {
            id: 'hr-jobs',
            label: t('nav.jobs', 'Jobs'),
            icon: <Icons.Briefcase />,
            path: '/positions/jobs',
            color: 'hr',
          },
          {
            id: 'hr-assignments',
            label: t('nav.assignments', 'Assignments'),
            icon: <Icons.UserGroup />,
            path: '/positions/assignments',
            color: 'hr',
          },
          {
            id: 'hr-grades',
            label: t('nav.grades', 'Grades'),
            icon: <Icons.ClipboardList />,
            path: '/positions/grades',
            color: 'hr',
          },
          // Settings
          {
            id: 'hr-settings',
            label: t('nav.basicData', 'Basic Data'),
            icon: <Icons.Database />,
            color: 'hr',
            dividerBefore: true,
            children: [
              {
                id: 'hr-employment-types',
                label: t('nav.employmentTypes', 'Employment Types'),
                icon: <Icons.Briefcase />,
                path: '/settings/hr/employment-types',
                color: 'hr',
              },
              {
                id: 'hr-cost-centers',
                label: t('nav.costCenters', 'Cost Centers'),
                icon: <Icons.Database />,
                path: '/settings/hr/cost-centers',
                color: 'hr',
              },
              {
                id: 'hr-holidays',
                label: t('nav.holidays', 'Holiday Calendar'),
                icon: <Icons.CalendarDays />,
                path: '/settings/hr/holidays',
                color: 'hr',
              },
              {
                id: 'expense-fx-rates',
                label: t('nav.fxRates', 'Exchange Rates'),
                icon: <Icons.Database />,
                path: '/settings/expense/fx-rates',
                color: 'hr',
              },
              {
                id: 'hr-absence-types',
                label: t('nav.absenceTypes', 'Absence Types'),
                icon: <Icons.CalendarOff />,
                path: '/settings/hr/absence-types',
                color: 'hr',
              },
              {
                id: 'hr-travel-policies',
                label: t('nav.travelPolicies', 'Travel Policies'),
                icon: <Icons.FileText />,
                path: '/settings/hr/travel-policies',
                color: 'travel',
              },
              {
                id: 'hr-expense-types',
                label: t('nav.expenseTypes', 'Expense Types'),
                icon: <Icons.Tags />,
                path: '/settings/hr/expense-types',
                color: 'expense',
              },
              {
                id: 'hr-course-mgmt',
                label: t('nav.courseManagement', 'Course Management'),
                icon: <Icons.Book />,
                path: '/training/admin/courses',
                color: 'training',
              },
              {
                id: 'hr-session-mgmt',
                label: t('nav.sessionManagement', 'Session Management'),
                icon: <Icons.CalendarDays />,
                path: '/training/admin/sessions',
                color: 'training',
              },
            ],
          },
        ],
      });
    }

    // Time & Attendance
    if (accessibleModules.includes('TIME')) {
      items.push({
        id: 'time',
        label: t('nav.time', 'Time & Attendance'),
        icon: <Icons.CalendarDays />,
        color: 'hr',
        children: [
          { id: 'time-mine', label: t('nav.myTimesheet', 'My Timesheet'), icon: <Icons.CalendarDays />, path: '/time', color: 'hr' },
          { id: 'time-team', label: t('nav.teamTimesheets', 'Team Timesheets'), icon: <Icons.CalendarDays />, path: '/time/team', color: 'hr' },
          { id: 'time-schedules', label: t('nav.workSchedules', 'Work Schedules'), icon: <Icons.CalendarDays />, path: '/time/schedules', color: 'hr', dividerBefore: true },
        ],
      });
    }

    // Survey & Feedback
    if (accessibleModules.includes('SRV')) {
      items.push({
        id: 'survey',
        label: t('nav.surveys', 'Surveys'),
        icon: <Icons.ClipboardList />,
        color: 'hr',
        children: [
          {
            id: 'survey-mine',
            label: t('nav.mySurveys', 'My Surveys'),
            icon: <Icons.ClipboardList />,
            path: '/surveys/assigned',
            color: 'hr',
          },
          {
            id: 'survey-list',
            label: t('nav.surveyList', 'Manage Surveys'),
            icon: <Icons.ClipboardList />,
            path: '/surveys',
            color: 'hr',
            dividerBefore: true,
          },
        ],
      });
    }

    // Document Management
    if (accessibleModules.includes('DOC')) {
      items.push({
        id: 'documents',
        label: t('nav.documents', 'Documents'),
        icon: <Icons.Folder />,
        color: 'documents',
        requiredModule: 'DOC',
        children: [
          {
            id: 'doc-library',
            label: t('documents.nav.library', 'Library'),
            icon: <Icons.Folder />,
            path: '/documents',
            color: 'documents',
          },
          {
            id: 'doc-shared',
            label: t('documents.nav.shared', 'Shared with me'),
            icon: <Icons.FolderShared />,
            path: '/documents/shared',
            color: 'documents',
          },
        ],
      });
    }

    // Service (field service / service desk) — role-aware: staff see the desk,
    // customers see their own portal.
    if (accessibleModules.includes('SVC')) {
      const svcRoles = user?.modules?.SVC?.roles ?? [];
      const isSvcStaff = svcRoles.some(r => ['SVC_ADMIN', 'SVC_DISPATCH', 'SVC_AGENT', 'SVC_TECH'].includes(r));
      if (isSvcStaff) {
        items.push({
          id: 'service',
          label: t('nav.service', 'Service'),
          icon: <Icons.Wrench />,
          color: 'service',
          requiredModule: 'SVC',
          children: [
            { id: 'svc-dashboard', label: t('nav.serviceDashboard', 'Dashboard'), icon: <Icons.Dashboard />, path: '/service', color: 'service' },
            { id: 'svc-requests', label: t('nav.serviceRequests', 'Service Requests'), icon: <Icons.ClipboardList />, path: '/service/requests', color: 'service' },
            { id: 'svc-work-orders', label: t('nav.serviceWorkOrders', 'Work Orders'), icon: <Icons.Wrench />, path: '/service/work-orders', color: 'service' },
            { id: 'svc-part-orders', label: t('nav.servicePartOrders', 'Part Orders'), icon: <Icons.Package />, path: '/service/part-orders', color: 'service' },
            { id: 'svc-catalogs', label: t('nav.serviceCatalogs', 'Catalogs'), icon: <Icons.Tags />, path: '/service/catalogs', color: 'service' },
          ],
        });
      } else {
        // Customer portal
        items.push({
          id: 'service',
          label: t('nav.service', 'Service'),
          icon: <Icons.Wrench />,
          color: 'service',
          requiredModule: 'SVC',
          children: [
            { id: 'svc-portal-products', label: t('nav.serviceMyProducts', 'My Products'), icon: <Icons.Package />, path: '/service/portal', color: 'service' },
            { id: 'svc-portal-requests', label: t('nav.serviceMyRequests', 'My Requests'), icon: <Icons.ClipboardList />, path: '/service/portal/requests', color: 'service' },
          ],
        });
      }
    }

    // Bid Management
    if (accessibleModules.includes('BID')) {
      items.push({
        id: 'bid',
        label: t('nav.bidManagement', 'Bid Management'),
        icon: <Icons.Gavel />,
        path: '/bidding',
        color: 'bid',
      });
    }

    // Reports
    if (accessibleModules.includes('RPT')) {
      items.push({
        id: 'reports',
        label: t('nav.reports', 'Reports'),
        icon: <Icons.BarChart />,
        path: '/reports',
        color: 'reports',
      });
    }

    // Platform Admin Section (only for platform admins)
    if (user?.isPlatformAdmin) {
      items.push({
        id: 'platform-admin',
        label: t('nav.platformAdmin', 'Platform Admin'),
        icon: <Icons.Settings />,
        color: 'enterprise',
        dividerBefore: true,
        children: [
          {
            id: 'admin-modules',
            label: t('nav.clientModules', 'Client Modules'),
            icon: <Icons.Package />,
            path: '/admin/modules',
            color: 'enterprise',
          },
          {
            id: 'admin-users',
            label: t('nav.userManagement', 'User Management'),
            icon: <Icons.Users />,
            path: '/admin/users',
            color: 'enterprise',
          },
          {
            id: 'admin-branding',
            label: t('nav.brandingManagement', 'Branding'),
            icon: <Icons.Palette />,
            path: '/admin/branding',
            color: 'enterprise',
          },
          {
            id: 'admin-integrations',
            label: t('nav.integrations', 'Integrations'),
            icon: <Icons.Link />,
            path: '/admin/integrations',
            color: 'enterprise',
          },
          {
            id: 'admin-api-docs',
            label: t('nav.apiDocs', 'API Documentation'),
            icon: <Icons.FileText />,
            path: '/admin/integrations/api-docs',
            color: 'enterprise',
          },
        ],
      });
    }

    return items;
  }, [t, accessibleModules, user?.isPlatformAdmin]);

  // Account settings items
  const accountItems = useMemo((): NavItem[] => [
    {
      id: 'account-settings',
      label: t('nav.accountSettings', 'Account Settings'),
      icon: <Icons.Settings />,
      color: 'settings',
      children: [
        {
          id: 'settings-profile',
          label: t('nav.profile', 'Profile'),
          icon: <Icons.User />,
          path: '/settings/profile',
          color: 'settings',
        },
        {
          id: 'settings-preferences',
          label: t('nav.preferences', 'Preferences'),
          icon: <Icons.Settings />,
          path: '/settings/preferences',
          color: 'settings',
        },
      ],
    },
  ], [t]);

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-40 flex flex-col bg-white dark:bg-[#1f1f1f] border-r border-slate-200/80 dark:border-[#3c4043]/80 shadow-sm transition-all duration-300 ease-out ${
        collapsed ? 'w-[72px]' : 'w-[280px]'
      }`}
    >
      {/* Logo Section */}
      <div className={`relative border-b border-slate-100 dark:border-[#3c4043] ${collapsed ? 'h-16 flex items-center justify-center px-3' : 'py-4 px-4'}`}>
        <div className={`flex ${collapsed ? 'items-center justify-center' : 'flex-col items-center gap-2'}`}>
          {/* Logo icon - show branding icon if available */}
          <div className="relative flex-shrink-0">
            {branding?.iconUrl ? (
              <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center shadow-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                <img
                  src={branding.iconUrl}
                  alt={branding.companyDisplayName || 'Logo'}
                  className="w-8 h-8 object-contain"
                />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-guma-500 to-guma-600 flex items-center justify-center shadow-lg shadow-guma-500/20">
                <img
                  src="https://www.gumahub.com/assets/gumahub-Hx4eram0.png"
                  alt="G"
                  className="h-6 brightness-0 invert"
                />
              </div>
            )}
          </div>
          {/* Company name and powered by text */}
          {!collapsed && (
            <div className="flex flex-col items-center text-center">
              {branding?.companyDisplayName ? (
                <>
                  <span className="text-base font-bold text-slate-900 dark:text-[#e8eaed] tracking-tight">
                    {branding.companyDisplayName}
                  </span>
                  <span className="text-[9px] font-light text-slate-400 dark:text-[#9aa0a6] tracking-wide">
                    powered by <span className="font-medium">GumaHUB</span>
                  </span>
                </>
              ) : (
                <>
                  <span className="text-lg font-bold text-slate-900 dark:text-[#e8eaed] tracking-tight">GumaHUB</span>
                  <span className="text-[10px] font-semibold text-slate-400 dark:text-[#9aa0a6] uppercase tracking-widest">Enterprise</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Collapse button */}
        <button
          onClick={onToggleCollapse}
          className={`absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white dark:bg-[#2d2d2d] border border-slate-200 dark:border-[#3c4043] shadow-sm flex items-center justify-center text-slate-400 dark:text-[#9aa0a6] hover:text-slate-600 dark:hover:text-[#e8eaed] hover:border-slate-300 dark:hover:border-[#5f6368] transition-all duration-200 hover:scale-110`}
        >
          {collapsed ? <Icons.PanelLeftOpen /> : <Icons.PanelLeftClose />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 scrollbar-thin">
        <NavSection
          items={navigationItems}
          collapsed={collapsed}
          expandedGroups={expandedGroups}
          onToggleGroup={toggleGroup}
        />
      </nav>

      {/* Account Section */}
      <div className="border-t border-slate-100 dark:border-[#3c4043]">
        <div className="p-2">
          <NavSection
            items={accountItems}
            collapsed={collapsed}
            expandedGroups={expandedGroups}
            onToggleGroup={toggleGroup}
          />
        </div>

        {/* User info */}
        <div className={`p-2 border-t border-slate-100 dark:border-[#3c4043] ${collapsed ? 'px-2' : 'px-3'}`}>
          <div className={`flex items-center gap-3 p-2 rounded-xl bg-gradient-to-r from-slate-50 to-white dark:from-[#2d2d2d] dark:to-[#1f1f1f] ${collapsed ? 'justify-center' : ''}`}>
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 dark:from-[#5f6368] dark:to-[#3c4043] flex items-center justify-center text-white font-semibold text-sm shadow-md">
                {user?.displayName?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-mint-500 rounded-full border-2 border-white dark:border-[#1f1f1f]" />
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-[#e8eaed] truncate">{user?.displayName}</p>
                <p className="text-xs text-slate-500 dark:text-[#9aa0a6] truncate">{user?.clientName}</p>
              </div>
            )}
          </div>

          {/* Logout button */}
          <button
            onClick={handleLogout}
            className={`mt-2 w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-500 dark:text-[#9aa0a6] hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200 ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            <Icons.LogOut />
            {!collapsed && <span className="text-sm font-medium">{t('nav.signOut', 'Sign Out')}</span>}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default NavigationSidebar;
